import { useImmerReducer } from 'use-immer';
import AppContext, { APP_INITIAL_STATE, appReducer } from './utils/AppContext';
import { Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import useColorTheme from './hooks/useColorTheme';
import { IS_DEV, IS_DEV_QA } from './utils/envSettings';

import SignupBare from './routes/Auth/SignupBare';
import Login from './routes/Auth/Login';
import SignUpOnboarding from './routes/Auth/Signup';
import AuthRoute from './routes/Auth/AuthRoute';
import Account from './routes/MainSettings/Account';
import Logout from './routes/Auth/Logout';
import LeadsPage from './routes/Leads/LeadsPage';
import CategorySettings from './routes/Settings/CategorySettings';
import Health from './routes/Health';
import ResetPassword from './routes/Auth/ResetPassword';
import Users from './routes/Users/Users';
import UserDetails from './routes/Users/UserDetails';
import BusinessSettings from './routes/Business/BusinessSettings';
import UserInvitation from './routes/Users/UserInvitation';
import OAuthConsent from './routes/Auth/OAuthConsent';
import Dashboard from './routes/Dashboard/Dashboard';
import Onboarding from './routes/Onboarding/Onboarding';
import PricingTiers from './routes/Pricing/PricingTiers';
import Organization from './routes/MainSettings/Organization';
import Billing from './routes/MainSettings/Billing';
import SkipOnboarding from './routes/Onboarding/SkipOnboarding';
import WIP from './common/WIP';

import UserColors from './routes/Dev/UserColors';
import DevWidget from './common/DevWidget/DevWidget';
import KanbanOrder from './routes/Dev/KanbanOrder';
import HelpWidget from './common/HelpWidget/HelpWidget';
import GlobalToast from './common/Toast/GlobalToast';

import './common/Modal.scss';
import './common/Tooltip.scss';

const LOCAL_OPTIONS = {
  defaultOptions: {
    queries   : { networkMode: 'always' },
    mutations : { networkMode: 'always' }
  }
};

const queryClient = new QueryClient(IS_DEV ? LOCAL_OPTIONS : undefined);

export default function App() {

  const appContext = useImmerReducer(appReducer, APP_INITIAL_STATE);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContext.Provider value={appContext}>

        <ColorTheme />
        { IS_DEV_QA && <DevWidget /> }
        <HelpWidget />
        <GlobalToast />

        <Routes>
          <Route path='health' element={<Health />} />

          {/* PUBLIC ROUTES */}
          <Route path='login' element={<AuthRoute publicOnly element={<Login />} />} />
          <Route path='signup' element={<AuthRoute publicOnly element={<SignUpOnboarding />} />} />
          <Route path='register' element={<AuthRoute publicOnly element={<SignupBare />} />} />
          <Route path='reset' element={<AuthRoute publicOnly element={<ResetPassword />} />} />
          <Route path='onboarding' element={<AuthRoute onboardingVisible element={<Onboarding />} />} />
          <Route path='skip-onboarding' element={<AuthRoute onboardingVisible element={<SkipOnboarding />} />} />
          <Route path='pricing' element={<PricingTiers />} />

          {/* LOGGED-IN ROUTES */}
          <Route path="/" element={<AuthRoute navigation liveUpdates element={<Dashboard />} />} />
          <Route path="profile" element={<AuthRoute navigation liveUpdates element={<Account />} />} />
          <Route path="billing" element={<AuthRoute navigation liveUpdates element={<Billing />} />} />
          <Route path="organization" element={<AuthRoute navigation liveUpdates element={<Organization />} />} />
          <Route path="logout" element={<AuthRoute onboardingVisible element={<Logout />} />} />
          <Route path="auth/authorize" element={<AuthRoute element={<OAuthConsent />} />} />

          <Route path="leads" element={<AuthRoute navigation liveUpdates element={<LeadsPage />} />} />
          <Route path="leads/:categoryId" element={<AuthRoute navigation liveUpdates element={<LeadsPage />} />} />
          <Route path="leads/settings/:categoryId" element={<AuthRoute navigation liveUpdates element={<CategorySettings />} />} />

          {/* WIP ROUTES */}
          <Route path="settings" element={<AuthRoute navigation liveUpdates element={<BusinessSettings />} />} />
          <Route path="reporting" element={<AuthRoute navigation liveUpdates element={<WIP />} />} />
          <Route path="users" element={<AuthRoute navigation liveUpdates element={<Users />} />} />
          <Route path="users/invite" element={<AuthRoute navigation liveUpdates element={<UserDetails newUser />} />} />
          <Route path="user-roles/:roleId" element={<AuthRoute navigation liveUpdates element={<UserDetails />} />} />

          <Route path='invite' element={<AuthRoute noRedirect element={<UserInvitation onboardingVisible /> } />} />

          {/* DEV */}
          <Route path="dev/user-colors" element={<UserColors />} />
          <Route path="dev/kanban-order" element={<KanbanOrder />} />
          <Route path="no-redirect" element={<AuthRoute noRedirect element={<div>This element should not redirect for logged out users</div>} />} />

          { /* LANGUAGE-DEPENDENT ROUTES */}
          <Route path=':lang/login' element={<AuthRoute publicOnly element={<Login />} />} />
          <Route path=':lang/signup' element={<AuthRoute publicOnly element={<SignUpOnboarding />} />} />
          <Route path=':lang/register' element={<AuthRoute publicOnly element={<SignupBare />} />} />
          <Route path=':lang/reset' element={<AuthRoute publicOnly element={<ResetPassword />} />} />

          {/* CATCH-ALL */}
          <Route path="*" element={<AuthRoute navigation element={<Dashboard />} />} />
        </Routes>
        </AppContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} position='bottom-right' />
    </QueryClientProvider>
  );
}

function ColorTheme() {
  useColorTheme();
}
